// src/features/townBudget/components/programs/Program_Town.jsx
import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Town Program (detailed department rollup)
 *
 * Backed by Firestore docId:
 *   expenditures_town
 *
 * NOTE:
 * This doc is structured as nested "category blocks" (e.g., "Leisure & Culture",
 * "Public Safety", etc.) each containing department line items.
 * So our fieldDefs point at { block: "<Category Name>", key: "<Dept Name>" }.
 */
export const PROGRAM_TOWN_CONFIG = {
  fullName: "Town",
  docId: "expenditures_town",

  fieldDefs: [
    // -------------------------
    // Leisure & Culture
    // -------------------------
    {
      key: "parksRecreation",
      label: "Parks/Recreation",
      valueType: "currency",
      sources: [{ block: "Leisure & Culture", key: "Parks/Recreation" }],
      group: "leisureAndCulture",
      defaultVisible: false,
    },
    {
      key: "wellesTurnerLibrary",
      label: "Welles Turner Library",
      valueType: "currency",
      sources: [{ block: "Leisure & Culture", key: "Welles Turner Library" }],
      group: "leisureAndCulture",
      defaultVisible: false,
    },
    {
      key: "southGlastonburyLibrary",
      label: "South Glastonbury Library",
      valueType: "currency",
      sources: [{ block: "Leisure & Culture", key: "South Glastonbury Library" }],
      group: "leisureAndCulture",
      defaultVisible: false,
    },
    {
      key: "eastGlastonburyLibrary",
      label: "East Glastonbury Library",
      valueType: "currency",
      sources: [{ block: "Leisure & Culture", key: "East Glastonbury Library" }],
      group: "leisureAndCulture",
      defaultVisible: false,
    },

    // -------------------------
    // Human Services
    // -------------------------
    {
      key: "healthGrants",
      label: "Health Grants",
      valueType: "currency",
      sources: [{ block: "Human Services", key: "Health Grants" }],
      group: "humanServices",
      defaultVisible: false,
    },
    {
      key: "youthFamilyServices",
      label: "Youth/Family Services",
      valueType: "currency",
      sources: [{ block: "Human Services", key: "Youth/Family Services" }],
      group: "humanServices",
      defaultVisible: false,
    },
    {
      key: "seniorCommunityServices",
      label: "Senior Community Services",
      valueType: "currency",
      sources: [{ block: "Human Services", key: "Senior Community Services" }],
      group: "humanServices",
      defaultVisible: false,
    },

    // -------------------------
    // Sanitation
    // -------------------------
    {
      key: "refuseDisposal",
      label: "Refuse Disposal",
      valueType: "currency",
      sources: [{ block: "Sanitation", key: "Refuse Disposal" }],
      group: "sanitation",
      defaultVisible: false,
    },

    // -------------------------
    // Physical Services
    // -------------------------
    {
      key: "engineering",
      label: "Engineering",
      valueType: "currency",
      sources: [{ block: "Physical Services", key: "Engineering" }],
      group: "physicalServices",
      defaultVisible: false,
    },
    {
      key: "highway",
      label: "Highway",
      valueType: "currency",
      sources: [{ block: "Physical Services", key: "Highway" }],
      group: "physicalServices",
      defaultVisible: false,
    },
    {
      key: "fleetMaintenance",
      label: "Fleet Maintenance",
      valueType: "currency",
      sources: [{ block: "Physical Services", key: "Fleet Maintenance" }],
      group: "physicalServices",
      defaultVisible: false,
    },

    // -------------------------
    // Public Safety
    // -------------------------
    {
      key: "police",
      label: "Police",
      valueType: "currency",
      sources: [{ block: "Public Safety", key: "Police" }],
      group: "publicSafety",
      defaultVisible: false,
    },
    {
      key: "volunteerAmbulance",
      label: "Volunteer Ambulance",
      valueType: "currency",
      sources: [{ block: "Public Safety", key: "Volunteer Ambulance" }],
      group: "publicSafety",
      defaultVisible: false,
    },
    {
      key: "fire",
      label: "Fire",
      valueType: "currency",
      sources: [{ block: "Public Safety", key: "Fire" }],
      group: "publicSafety",
      defaultVisible: false,
    },
    {
      key: "civilPreparedness",
      label: "Civil Preparedness",
      valueType: "currency",
      sources: [{ block: "Public Safety", key: "Civil Preparedness" }],
      group: "publicSafety",
      defaultVisible: false,
    },

    // -------------------------
    // Administrative Services
    // -------------------------
    {
      key: "financialAdministration",
      label: "Financial Administration",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Financial Administration" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "accounting",
      label: "Accounting",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Accounting" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "propertyAssessment",
      label: "Property Assessment",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Property Assessment" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "revenueCollection",
      label: "Revenue Collection",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Revenue Collection" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "townClerk",
      label: "Town Clerk",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Town Clerk" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "voterRegistration",
      label: "Voter Registration",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Voter Registration" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "legalServices",
      label: "Legal Services",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Legal Services" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "probateServices",
      label: "Probate Services",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Probate Services" }],
      group: "administrativeServices",
      defaultVisible: false,
    },
    {
      key: "insurancePensions",
      label: "Insurance Pensions",
      valueType: "currency",
      sources: [{ block: "Administrative Services", key: "Insurance Pensions" }],
      group: "administrativeServices",
      defaultVisible: false,
    },

    // -------------------------
    // Community Development
    // -------------------------
    {
      key: "communityDevelopment",
      label: "Community Development",
      valueType: "currency",
      sources: [{ block: "Community Development", key: "Community Development" }],
      group: "communityDevelopment",
      defaultVisible: false,
    },
    {
      key: "buildingInspection",
      label: "Building Inspection",
      valueType: "currency",
      sources: [{ block: "Community Development", key: "Building Inspection" }],
      group: "communityDevelopment",
      defaultVisible: false,
    },
    {
      key: "fireMarshal",
      label: "Fire Marshal",
      valueType: "currency",
      sources: [{ block: "Community Development", key: "Fire Marshal" }],
      group: "communityDevelopment",
      defaultVisible: false,
    },
    {
      key: "health",
      label: "Health",
      valueType: "currency",
      sources: [{ block: "Community Development", key: "Health" }],
      group: "communityDevelopment",
      defaultVisible: false,
    },

    // -------------------------
    // General Government
    // -------------------------
    {
      key: "townCouncil",
      label: "Town Council",
      valueType: "currency",
      sources: [{ block: "General Government", key: "Town Council" }],
      group: "generalGovernment",
      defaultVisible: false,
    },
    {
      key: "townManager",
      label: "Town Manager",
      valueType: "currency",
      sources: [{ block: "General Government", key: "Town Manager" }],
      group: "generalGovernment",
      defaultVisible: false,
    },
    {
      key: "humanResources",
      label: "Human Resources",
      valueType: "currency",
      sources: [{ block: "General Government", key: "Human Resources" }],
      group: "generalGovernment",
      defaultVisible: false,
    },
    {
      key: "facilitiesMaintenance",
      label: "Facilities Maintenance",
      valueType: "currency",
      sources: [{ block: "General Government", key: "Facilities Maintenance" }],
      group: "generalGovernment",
      defaultVisible: false,
    },
  ],

  tables: [
    {
      id: "adopted",
      title: "Town – Approved",
      sectionKey: "Approved",
      fields: [
        // General Government
        "townCouncil",
        "townManager",
        "humanResources",
        "facilitiesMaintenance",

        // Community Development
        "communityDevelopment",
        "buildingInspection",
        "fireMarshal",
        "health",

        // Administrative Services
        "financialAdministration",
        "accounting",
        "propertyAssessment",
        "revenueCollection",
        "townClerk",
        "voterRegistration",
        "legalServices",
        "probateServices",
        "insurancePensions",

        // Public Safety
        "police",
        "volunteerAmbulance",
        "fire",
        "civilPreparedness",

        // Physical Services
        "engineering",
        "highway",
        "fleetMaintenance",

        // Sanitation
        "refuseDisposal",

        // Human Services
        "healthGrants",
        "youthFamilyServices",
        "seniorCommunityServices",

        // Leisure & Culture
        "parksRecreation",
        "wellesTurnerLibrary",
        "southGlastonburyLibrary",
        "eastGlastonburyLibrary",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        parksRecreation: { minWidth: "170px" },
        wellesTurnerLibrary: { minWidth: "190px" },
        southGlastonburyLibrary: { minWidth: "210px" },
        eastGlastonburyLibrary: { minWidth: "200px" },

        youthFamilyServices: { minWidth: "210px" },
        seniorCommunityServices: { minWidth: "230px" },

        refuseDisposal: { minWidth: "150px" },

        fleetMaintenance: { minWidth: "180px" },

        volunteerAmbulance: { minWidth: "190px" },
        civilPreparedness: { minWidth: "190px" },

        financialAdministration: { minWidth: "220px" },
        propertyAssessment: { minWidth: "180px" },
        revenueCollection: { minWidth: "180px" },
        voterRegistration: { minWidth: "170px" },
        legalServices: { minWidth: "150px" },
        probateServices: { minWidth: "160px" },
        insurancePensions: { minWidth: "190px" },

        buildingInspection: { minWidth: "180px" },
        fireMarshal: { minWidth: "150px" },

        townManager: { minWidth: "150px" },
        humanResources: { minWidth: "160px" },
        facilitiesMaintenance: { minWidth: "210px" },
      },
    },
    {
      id: "actual",
      title: "Town – Actual",
      sectionKey: "Actual",
      fields: [
        // General Government
        "townCouncil",
        "townManager",
        "humanResources",
        "facilitiesMaintenance",

        // Community Development
        "communityDevelopment",
        "buildingInspection",
        "fireMarshal",
        "health",

        // Administrative Services
        "financialAdministration",
        "accounting",
        "propertyAssessment",
        "revenueCollection",
        "townClerk",
        "voterRegistration",
        "legalServices",
        "probateServices",
        "insurancePensions",

        // Public Safety
        "police",
        "volunteerAmbulance",
        "fire",
        "civilPreparedness",

        // Physical Services
        "engineering",
        "highway",
        "fleetMaintenance",

        // Sanitation
        "refuseDisposal",

        // Human Services
        "healthGrants",
        "youthFamilyServices",
        "seniorCommunityServices",

        // Leisure & Culture
        "parksRecreation",
        "wellesTurnerLibrary",
        "southGlastonburyLibrary",
        "eastGlastonburyLibrary",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        parksRecreation: { minWidth: "170px" },
        wellesTurnerLibrary: { minWidth: "190px" },
        southGlastonburyLibrary: { minWidth: "210px" },
        eastGlastonburyLibrary: { minWidth: "200px" },

        youthFamilyServices: { minWidth: "210px" },
        seniorCommunityServices: { minWidth: "230px" },

        refuseDisposal: { minWidth: "150px" },

        fleetMaintenance: { minWidth: "180px" },

        volunteerAmbulance: { minWidth: "190px" },
        civilPreparedness: { minWidth: "190px" },

        financialAdministration: { minWidth: "220px" },
        propertyAssessment: { minWidth: "180px" },
        revenueCollection: { minWidth: "180px" },
        voterRegistration: { minWidth: "170px" },
        legalServices: { minWidth: "150px" },
        probateServices: { minWidth: "160px" },
        insurancePensions: { minWidth: "190px" },

        buildingInspection: { minWidth: "180px" },
        fireMarshal: { minWidth: "150px" },

        townManager: { minWidth: "150px" },
        humanResources: { minWidth: "160px" },
        facilitiesMaintenance: { minWidth: "210px" },
      },
    },
  ],

  chartTitle: "Town Expenditures (Actual preferred)",
};

export function ProgramTown(props) {
  return (
    <ProgramBudget
      programId="expenditures_leisure_and_culture"
      config={PROGRAM_TOWN_CONFIG}
      {...props}
    />
  );
}

export default ProgramTown;
